import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = [
    "form",
    "paymentElement",
    "submit",
    "spinner",
    "buttonText",
    "message",
    "selectedPaymentMethodId",
    "paymentMethodSelect",
    "addNewLink",
    "newPaymentSection",
    "creditCardSection",
    "savedPaymentSection",
    "paymentType",
    "directedGivingSection",
    "directedGivingForm",
    "directedGivingButton",
    "directedGivingFundName"
  ]

  static values = {
    publishableKey: String,
    returnUrl: String,
    totalAmount: Number,
    clientSecret: String,
    intentId: String
  }

  async connect() {
    console.log('connect stripe payments controller')
    this.submitting = false
    this.isFormValid = false
    this.usingSavedMethod = false
    this.selectedPaymentMethodId = null
    this.usingDirectedGiving = false
    this.isAchPayment = null // null so first change event triggers capture_method update
    this.defaultButtonText = 'REVIEW DONATION'

    // Check if we have saved payment methods selected by default
    this.initializeSavedMethodSelection()

    // Guard against missing Stripe.js or publishable key
    if (typeof Stripe === 'undefined') {
      console.error('Stripe.js not loaded')
      this.showMessage('Payment system not available. Please refresh the page.')
      return
    }

    if (!this.publishableKeyValue) {
      console.error('No Stripe publishable key provided')
      this.showMessage('Payment configuration error. Please contact support.')
      return
    }

    this.stripe = Stripe(this.publishableKeyValue)
    this.initialize()
  }

  // Get the submit button - may be outside controller scope on mobile due to navigation repositioning
  getSubmitButton() {
    if (this.hasSubmitTarget) {
      return this.submitTarget
    }
    // Fallback: search in mobile nav placeholder
    const mobileNav = document.getElementById('mobile-nav-placeholder')
    if (mobileNav) {
      return mobileNav.querySelector('[data-payment-target="submit"]')
    }
    return null
  }

  async initialize() {
    console.log('initialize stripe payments controller')

    // Guard against stripe not being initialized
    if (!this.stripe) {
      console.error('Stripe not initialized')
      return
    }

    // Use the payment intent that was already created server-side
    // This is more secure as the amount is set server-side and can't be tampered with
    const clientSecret = this.clientSecretValue
    this.paymentIntentId = this.intentIdValue

    if (!clientSecret) {
      console.error('No client secret provided')
      this.showMessage('Payment initialization failed. Please refresh the page.')
      return
    }

    // Stripe Elements appearance matching our design system
    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#EE6840',
        colorBackground: '#ffffff',
        colorText: '#2C3441',
        colorDanger: '#dc3545',
        fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSizeBase: '15px',
        borderRadius: '5px',
        spacingUnit: '4px',
        spacingGridRow: '16px',
        spacingGridColumn: '16px'
      },
      rules: {
        '.Input': {
          border: '1px solid #CACCCF',
          boxShadow: 'none',
          padding: '12px',
          fontSize: '15px',
          lineHeight: '1.5'
        },
        '.Input:focus': {
          border: '1px solid #EE6840',
          boxShadow: '0 0 0 0.2rem rgba(238, 104, 64, 0.15)'
        },
        '.Input--invalid': {
          border: '1px solid #dc3545'
        },
        '.Label': {
          color: '#95999F',
          fontSize: '12px',
          fontWeight: '400',
          marginBottom: '4px'
        },
        '.Tab': {
          border: '1px solid #CACCCF',
          borderRadius: '5px'
        },
        '.Tab:hover': {
          border: '1px solid #EE6840'
        },
        '.Tab--selected': {
          border: '1px solid #EE6840',
          backgroundColor: '#FFF5F2'
        },
        '.Error': {
          color: '#dc3545',
          fontSize: '13px'
        }
      }
    }

    this.elements = this.stripe.elements({
      clientSecret,
      appearance,
      paymentMethodCreation: 'manual'
    })

    const paymentElementOptions = {
      layout: "accordion",
      // Disable wallets in Payment Element - we use Express Checkout Element for wallets
      wallets: {
        applePay: 'never',
        googlePay: 'never'
      },
      defaultValues: {
        billingDetails: this.getBillingDetails()
      }
    }

    const paymentElement = this.elements.create("payment", paymentElementOptions)
    paymentElement.mount("#payment-element")

    // Disable submit button initially, unless a saved payment method is already selected
    if (!this.usingSavedMethod) {
      const submitButton = this.getSubmitButton()
      if (submitButton) submitButton.disabled = true
    }

    // Listen for changes in the Payment Element to validate
    paymentElement.on('change', (event) => {
      this.handleFormValidation(event)
    })
  }

  handleFormValidation(event) {
    // Get the validation status from the event
    this.isFormValid = event.complete

    // Enable or disable the submit button based on validation
    // But only if we're NOT using a saved payment method
    if (!this.usingSavedMethod) {
      const submitButton = this.getSubmitButton()
      if (submitButton) submitButton.disabled = !this.isFormValid
    }

    // Detect payment method type change (card vs bank account)
    if (event.value && event.value.type) {
      const isAch = event.value.type === 'us_bank_account'
      if (isAch !== this.isAchPayment) {
        this.isAchPayment = isAch
        this.updateButtonTextForPaymentType()
        this.updatePaymentMethodType(isAch ? 'us_bank_account' : 'card')
      }
    }

    // Show error message if there's a validation error
    if (event.error) {
      this.showMessage(event.error.message)
    } else {
      // Clear any previous error messages
      if (this.hasMessageTarget) {
        this.messageTarget.textContent = ""
        this.messageTarget.classList.add('hidden', 'd-none')
      }
    }
  }

  async handleSubmit(event) {
    event.preventDefault()
    if (this.submitting) return

    // Skip if this is a wallet payment (Apple Pay/Google Pay) - already handled
    if (this.formTarget.dataset.walletPaymentConfirmed === 'true') {
      console.log('Payment: Skipping handleSubmit - wallet payment already confirmed')
      this.formTarget.submit()
      return
    }

    // Handle directed giving: skip Stripe API calls, just submit form
    if (this.usingDirectedGiving) {
      console.log('Payment: Directed giving selected, submitting form directly')
      if (this.hasPaymentTypeTarget) {
        this.paymentTypeTarget.value = 'directed_giving'
      }
      this.formTarget.submit()
      return
    }

    // Validate: either using saved method OR new payment form is valid
    if (!this.usingSavedMethod && !this.isFormValid) {
      this.showMessage("Please complete payment details before submitting.")
      return
    }

    // Update button to show processing state
    const submitButton = this.getSubmitButton()
    if (submitButton) {
      submitButton.disabled = true
      const spinnerText = this.isAchPayment ? 'Processing...' : 'Reviewing...'
      submitButton.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${spinnerText}`
    }
    this.submitting = true

    try {
      let paymentMethodId

      if (this.usingSavedMethod && this.selectedPaymentMethodId) {
        // Using a saved payment method - just use the existing ID
        console.log('Payment: Using saved payment method', this.selectedPaymentMethodId)
        paymentMethodId = this.selectedPaymentMethodId
      } else {
        // Creating a new payment method
        console.log('Payment: Creating new payment method')

        // First, submit the elements form
        // This is required by Stripe before calling createPaymentMethod
        const { error: submitError } = await this.elements.submit();

        if (submitError) {
          this.handleError(submitError)
          return
        }

        // Then create the payment method with the validated elements
        const { error, paymentMethod } = await this.stripe.createPaymentMethod({
          elements: this.elements,
          params: {
            billing_details: this.getBillingDetails()
          }
        });

        if (error) {
          this.handleError(error)
          return
        }

        paymentMethodId = paymentMethod.id
      }

      // Attach the payment method to the payment intent via our server
      const attachResponse = await fetch("/payments/attach_payment_method", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({
          payment_intent_id: this.paymentIntentId,
          payment_method_id: paymentMethodId
        })
      });

      if (!attachResponse.ok) {
        const errorData = await attachResponse.json();
        this.handleError({ message: errorData.error || "Failed to attach payment method" });
        return;
      }

      // If payment method attachment succeeds, proceed to review
      // We'll add a hidden field with the payment_intent_id
      const hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = 'payment_intent_id'
      hiddenField.value = this.paymentIntentId
      this.formTarget.appendChild(hiddenField)

      // Submit the form to proceed to review
      this.formTarget.submit()

    } catch (error) {
      this.handleError(error)
    }
  }

  // Update submit button text based on payment method type
  updateButtonTextForPaymentType() {
    if (this.hasButtonTextTarget) {
      if (this.isAchPayment) {
        this.buttonTextTarget.innerHTML = 'CONFIRM DONATION <i class="fa-solid fa-arrow-right ms-2"></i>'
      } else {
        this.buttonTextTarget.innerHTML = this.defaultButtonText + ' <i class="fa-solid fa-arrow-right ms-2"></i>'
      }
    }
  }

  // Notify server of payment method type change so fees are recalculated
  updatePaymentMethodType(paymentMethodType) {
    console.log('Payment: Updating payment method type to', paymentMethodType)

    fetch('/shopping_cart/update_payment_method_type', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ payment_method_type: paymentMethodType })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        console.log('Payment: Fees updated for', paymentMethodType, data.cart)
        // Dispatch cartUpdated event so purchase summary updates
        document.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cart: data.cart } }))
      } else {
        console.error('Payment: Failed to update payment method type:', data.error)
      }
    })
    .catch(error => {
      console.error('Payment: Error updating payment method type:', error)
    })
  }

  getBillingDetails() {
    const form = this.formTarget
    return {
      name: form.dataset.customerName,
      email: form.dataset.customerEmail,
      address: {
        line1: form.dataset.addressLine1,
        line2: form.dataset.addressLine2,
        city: form.dataset.city,
        state: form.dataset.state,
        postal_code: form.dataset.zipCode,
        country: 'US'
      }
    }
  }

  handleError(error) {
    // Reset button state
    this.restoreButtonState();

    // Get the error message
    const message = error.type === "card_error" || error.type === "validation_error" ?
      error.message : "An unexpected error occurred.";

    // Make sure the message element exists and is accessible
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = message;
      this.messageTarget.classList.remove('hidden', 'd-none');

      // Log for debugging
      console.error("Payment error:", error);
    } else {
      // Fallback if target not found
      console.error("Payment error (no target):", error, message);
      alert("Payment error: " + message);
    }
  }

  showMessage(messageText) {
    const messageTarget = this.messageTarget
    messageTarget.classList.remove("hidden", "d-none")
    messageTarget.textContent = messageText

    setTimeout(() => {
      messageTarget.classList.add("hidden", "d-none")
      messageTarget.textContent = ""
    }, 4000)
  }

  setLoading(isLoading) {
    this.submitting = isLoading
    const submitButton = this.getSubmitButton()
    if (submitButton) {
      submitButton.disabled = isLoading

      if (isLoading) {
        // Store original button content if needed
        this._originalButtonHTML = submitButton.innerHTML;
        // Set to processing state
        submitButton.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Processing...';
      } else {
        // Restore original button content
        this.restoreButtonState();
      }
    }
  }

  restoreButtonState() {
    // Reset submitting flag
    this.submitting = false;

    const submitButton = this.getSubmitButton()
    if (submitButton) {
      // If we have the original HTML saved, restore it
      if (this._originalButtonHTML) {
        submitButton.innerHTML = this._originalButtonHTML;
        this._originalButtonHTML = null;
      } else {
        // Fallback if original HTML wasn't saved
        submitButton.innerHTML = 'Submit Payment';
      }

      // Enable/disable based on form validation or saved method selection
      submitButton.disabled = !(this.isFormValid || this.usingSavedMethod);
    }
  }

  // Initialize saved payment method selection if any exist
  initializeSavedMethodSelection() {
    if (this.hasPaymentMethodSelectTarget) {
      // Initialize select2 on the payment method dropdown
      const $select = $(this.paymentMethodSelectTarget)
      $select.select2({
        minimumResultsForSearch: Infinity, // Disable search
        width: '100%'
      })

      // Handle select2 change event
      $select.on('select2:select', (e) => {
        this.handlePaymentMethodChange({ target: e.target })
      })

      // Initialize with current selection
      const selectedValue = $select.val()
      if (selectedValue && selectedValue !== 'new') {
        this.usingSavedMethod = true
        this.selectedPaymentMethodId = selectedValue
        if (this.hasSelectedPaymentMethodIdTarget) {
          this.selectedPaymentMethodIdTarget.value = selectedValue
        }
        // Enable submit button since a saved method is selected
        const submitButton = this.getSubmitButton()
        if (submitButton) {
          submitButton.disabled = false
        }
        console.log('Payment: Initialized with saved method', this.selectedPaymentMethodId)
      }
    }
  }

  // Handle payment method dropdown change
  handlePaymentMethodChange(event) {
    const selectedValue = event.target.value

    // User selected a saved payment method
    this.usingSavedMethod = true
    this.selectedPaymentMethodId = selectedValue
    if (this.hasSelectedPaymentMethodIdTarget) {
      this.selectedPaymentMethodIdTarget.value = selectedValue
    }

    // Enable submit button
    const submitButton = this.getSubmitButton()
    if (submitButton) submitButton.disabled = false

    console.log('Payment: Selected saved method', selectedValue)
  }

  // Show the new payment method form
  showNewPaymentForm(event) {
    event.preventDefault()

    // Clear saved method selection
    this.usingSavedMethod = false
    this.selectedPaymentMethodId = null
    if (this.hasSelectedPaymentMethodIdTarget) {
      this.selectedPaymentMethodIdTarget.value = ''
    }

    // Hide saved payment methods section and show new payment form
    if (this.hasPaymentMethodSelectTarget) {
      this.paymentMethodSelectTarget.closest('.gv-saved-payment-methods').classList.add('d-none')
    }
    if (this.hasCreditCardSectionTarget) {
      this.creditCardSectionTarget.classList.remove('d-none')
    }
    if (this.hasNewPaymentSectionTarget) {
      this.newPaymentSectionTarget.classList.remove('d-none')
    }

    // Disable submit button until new payment form is valid
    const submitBtn = this.getSubmitButton()
    if (submitBtn) submitBtn.disabled = !this.isFormValid

    console.log('Payment: Showing new payment form')
  }

  // Show saved payment methods (go back from new payment form)
  showSavedPaymentMethods(event) {
    event.preventDefault()

    // Restore saved method selection from dropdown
    if (this.hasPaymentMethodSelectTarget) {
      const selectedValue = this.paymentMethodSelectTarget.value
      if (selectedValue) {
        this.usingSavedMethod = true
        this.selectedPaymentMethodId = selectedValue
        if (this.hasSelectedPaymentMethodIdTarget) {
          this.selectedPaymentMethodIdTarget.value = selectedValue
        }
      }

      // Show saved payment methods section
      this.paymentMethodSelectTarget.closest('.gv-saved-payment-methods').classList.remove('d-none')
    }

    // Hide new payment form
    if (this.hasCreditCardSectionTarget) {
      this.creditCardSectionTarget.classList.add('d-none')
    }
    if (this.hasNewPaymentSectionTarget) {
      this.newPaymentSectionTarget.classList.add('d-none')
    }

    // Enable submit button since a saved method is selected
    const submitButton = this.getSubmitButton()
    if (submitButton) submitButton.disabled = false

    console.log('Payment: Showing saved payment methods')
  }

  // Select directed giving payment method
  selectDirectedGiving(event) {
    if (event) event.preventDefault()
    this.usingDirectedGiving = true
    this.isAchPayment = false

    // Hide Stripe payment sections
    const paymentMethodsCard = this.element.querySelector('.gv-payment-methods-card')
    if (paymentMethodsCard) paymentMethodsCard.classList.add('d-none')

    // Hide the DG button, show the DG form
    if (this.hasDirectedGivingSectionTarget) {
      this.directedGivingSectionTarget.classList.add('d-none')
    }
    if (this.hasDirectedGivingFormTarget) {
      this.directedGivingFormTarget.classList.remove('d-none')
    }

    // Enable submit button
    const submitButton = this.getSubmitButton()
    if (submitButton) submitButton.disabled = false

    // Update fees (directed giving has zero processing fees)
    this.updatePaymentMethodType('directed_giving')

    console.log('Payment: Directed giving selected')
  }

  // Switch back to Stripe payment
  selectStripePayment(event) {
    if (event) event.preventDefault()
    this.usingDirectedGiving = false

    // Show Stripe payment sections
    const paymentMethodsCard = this.element.querySelector('.gv-payment-methods-card')
    if (paymentMethodsCard) paymentMethodsCard.classList.remove('d-none')

    // Show the DG button section, hide the DG form
    if (this.hasDirectedGivingSectionTarget) {
      this.directedGivingSectionTarget.classList.remove('d-none')
    }
    if (this.hasDirectedGivingFormTarget) {
      this.directedGivingFormTarget.classList.add('d-none')
    }

    // Restore submit button state and text based on form validity
    const submitButton = this.getSubmitButton()
    if (submitButton) {
      submitButton.disabled = !(this.isFormValid || this.usingSavedMethod)
    }
    this.updateButtonTextForPaymentType()

    // Restore card fees
    this.updatePaymentMethodType(this.isAchPayment ? 'us_bank_account' : 'card')

    console.log('Payment: Switched back to Stripe payment')
  }
}
